import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import RelatedPages from '../components/RelatedPages';
import { getPublicAssetPath } from '../config';

const PrivacyPolicy: React.FC = () => {
  const { darkMode } = useTheme();
  
  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/dashboard" 
            className={`inline-flex items-center text-sm mb-6 transition-colors ${
              darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'
            }`}
          >
            <i className="fa-solid fa-arrow-left mr-2"></i>
            Kembali ke Dashboard
          </Link>
          <div className="flex items-center justify-center mb-6">
            <img src={getPublicAssetPath('/le_long.png')} alt="Legitimu Logo" className="h-12" style={darkMode ? { filter: 'brightness(0) invert(1)' } : {}} />
          </div>
          <h1 className={`text-3xl font-bold text-center mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Privacy Policy</h1>
          <p className={`text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Last updated: {new Date().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        {/* Content */}
        <div className={`rounded-lg border shadow-sm p-8 space-y-8 transition-colors duration-300 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <section>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>1. Introduction</h2>
            <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Legitimu ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Agreement Management System.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>2. Information We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>2.1 Personal Information</h3>
                <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  We may collect personal information that you provide to us, including but not limited to:
                </p>
                <ul className={`list-disc list-inside mt-2 space-y-1 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>Name and contact information (email address, phone number)</li>
                  <li>Company information and job title</li>
                  <li>Account credentials and authentication information</li>
                  <li>Profile information and preferences</li>
                </ul>
              </div>
              <div>
                <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>2.2 Usage Data</h3>
                <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  We automatically collect information about how you interact with our service, including:
                </p>
                <ul className={`list-disc list-inside mt-2 space-y-1 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>Log data and IP addresses</li>
                  <li>Device information and browser type</li>
                  <li>Usage patterns and feature interactions</li>
                  <li>Error logs and performance data</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>3. How We Use Your Information</h2>
            <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              We use the information we collect for various purposes, including:
            </p>
            <ul className={`list-disc list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <li>To provide, maintain, and improve our services</li>
              <li>To process transactions and manage agreements</li>
              <li>To send you notifications and updates</li>
              <li>To respond to your inquiries and provide customer support</li>
              <li>To detect, prevent, and address technical issues</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>4. Data Security</h2>
            <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>5. Data Sharing and Disclosure</h2>
            <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className={`list-disc list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <li>With your explicit consent</li>
              <li>To comply with legal obligations or court orders</li>
              <li>To protect our rights, privacy, safety, or property</li>
              <li>With service providers who assist us in operating our service</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>6. Your Rights</h2>
            <p className={`leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              You have the right to:
            </p>
            <ul className={`list-disc list-inside space-y-2 ml-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <li>Access and receive a copy of your personal data</li>
              <li>Rectify inaccurate or incomplete data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to processing of your personal data</li>
              <li>Request restriction of processing</li>
              <li>Data portability</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>7. Contact Us</h2>
            <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <div className={`mt-4 p-4 border rounded-lg transition-colors duration-300 ${
              darkMode ? 'bg-violet-900/30 border-violet-700' : 'bg-violet-50 border-violet-200'
            }`}>
              <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                <strong>Email:</strong> privacy@legitimu.com<br />
                <strong>Address:</strong> [Your Company Address]
              </p>
            </div>
          </section>
        </div>

        {/* Related Pages */}
        <RelatedPages currentPage="privacy" />

        {/* Footer Actions */}
        <div className="mt-8 text-center">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
          >
            <i className="fa-solid fa-arrow-left mr-2"></i>
            Kembali ke Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

